import { LIMITS, type Op } from "./op.js";

// In-memory op DAG. Persistence is the caller's concern (the PWA mirrors ops
// into IndexedDB); the store only guarantees graph invariants and deterministic
// traversal. add() assumes the op has already passed validateOp().

export class OpStore {
  private ops = new Map<string, Op>();
  private children = new Map<string, Set<string>>(); // parent hash -> child hashes
  private groupIdInternal = "";

  get groupId(): string {
    return this.groupIdInternal;
  }

  get size(): number {
    return this.ops.size;
  }

  has(hash: string): boolean {
    return this.ops.has(hash);
  }

  get(hash: string): Op | undefined {
    return this.ops.get(hash);
  }

  all(): Op[] {
    return [...this.ops.values()];
  }

  // Idempotent. Returns false when the op was already present.
  add(op: Op): boolean {
    if (this.ops.has(op.hash)) return false;
    if (op.kind === "genesis") {
      if (this.groupIdInternal !== "" && this.groupIdInternal !== op.hash) {
        throw new Error("store already bound to a different group");
      }
      this.groupIdInternal = op.hash;
    }
    this.ops.set(op.hash, op);
    for (const parent of op.parents) {
      let set = this.children.get(parent);
      if (!set) {
        set = new Set();
        this.children.set(parent, set);
      }
      set.add(op.hash);
    }
    return true;
  }

  // Current DAG frontier: ops with no children, sorted for determinism.
  heads(): string[] {
    const result: string[] = [];
    for (const hash of this.ops.keys()) {
      const kids = this.children.get(hash);
      if (!kids || kids.size === 0) result.push(hash);
    }
    return result.sort();
  }

  // Parent set for a NEW op: validation caps parents at LIMITS.parents, so a
  // pathological many-headed DAG must not brick appends. Leftover heads get
  // merged by subsequent ops.
  headsForNewOp(): string[] {
    return this.heads().slice(0, LIMITS.parents);
  }

  // Ancestor closure (inclusive) of the given hashes. Unknown hashes are skipped.
  ancestorsOf(hashes: string[]): Set<string> {
    const seen = new Set<string>();
    const stack = hashes.filter((h) => this.ops.has(h));
    while (stack.length > 0) {
      const hash = stack.pop() as string;
      if (seen.has(hash)) continue;
      seen.add(hash);
      const op = this.ops.get(hash);
      if (op) {
        for (const parent of op.parents) {
          if (!seen.has(parent) && this.ops.has(parent)) stack.push(parent);
        }
      }
    }
    return seen;
  }

  // Deterministic total order: Kahn's algorithm, always picking the ready op
  // with the smallest (lamport, hash). Every replica sorts identically, which
  // makes the reducer's output replica-independent.
  topoOrder(): Op[] {
    const pendingParents = new Map<string, number>();
    const ready: Op[] = [];
    for (const op of this.ops.values()) {
      const known = op.parents.filter((p) => this.ops.has(p)).length;
      pendingParents.set(op.hash, known);
      if (known === 0) ready.push(op);
    }
    const byOrder = (a: Op, b: Op) =>
      a.lamport !== b.lamport ? a.lamport - b.lamport : a.hash < b.hash ? -1 : 1;
    ready.sort(byOrder);
    const out: Op[] = [];
    while (ready.length > 0) {
      const op = ready.shift() as Op;
      out.push(op);
      const kids = this.children.get(op.hash);
      if (!kids) continue;
      for (const kidHash of [...kids].sort()) {
        const remaining = (pendingParents.get(kidHash) ?? 0) - 1;
        pendingParents.set(kidHash, remaining);
        if (remaining === 0) {
          const kid = this.ops.get(kidHash);
          if (kid) {
            // insert keeping `ready` sorted (groups are small; linear is fine)
            let i = 0;
            while (i < ready.length && byOrder(ready[i] as Op, kid) < 0) i++;
            ready.splice(i, 0, kid);
          }
        }
      }
    }
    if (out.length !== this.ops.size) {
      throw new Error("topoOrder: cycle or dangling parent detected");
    }
    return out;
  }

  // Ops the remote side is missing, given the heads it advertised. Heads we do
  // not recognize contribute nothing (we may over-send; ingest is idempotent).
  missingFor(remoteHeads: string[]): Op[] {
    const theyHave = this.ancestorsOf(remoteHeads);
    return this.topoOrder().filter((op) => !theyHave.has(op.hash));
  }
}
